<c-container class="dashboard-container" fluid>
  <!-- Card partner programme: topmost, and only for members who hold a card. -->
  <c-row class="mb-4">
    <c-col xs="12">
      <app-mycards-dashboard-link></app-mycards-dashboard-link>
    </c-col>
  </c-row>

  <c-row class="mb-4">
    <c-col xs="12">
      <h2 class="mb-5">{{ 'dashboard_welcome' | translate: { appName: ('app.name' | translate) } }}</h2>
    </c-col>
  </c-row>

  <!-- Account created by the Platinum direct checkout (no registration before the
       purchase): the e-mail is confirmed afterwards, so remind instead of blocking. -->
  @if (showVerifyBanner()) {
    <c-row class="mb-4">
      <c-col xs="12">
        <div class="alert alert-warning d-flex flex-column flex-md-row align-items-md-center gap-2 verify-banner" role="status">
          <div class="flex-grow-1">
            <strong>{{ 'verify_email_banner.title' | translate }}</strong>
            <div class="small">
              @switch (verifyMailState()) {
                @case ('sent') { {{ 'verify_email_banner.sent' | translate }} }
                @case ('already_confirmed') { {{ 'verify_email_banner.already_confirmed' | translate }} }
                @case ('error') { {{ 'verify_email_banner.error' | translate }} }
                @default { {{ 'verify_email_banner.body' | translate:{ email: user()?.email ?? '' } }} }
              }
            </div>
          </div>
          <div class="d-flex gap-2">
            <button cButton color="primary" size="sm" type="button"
                    [disabled]="verifyMailState() === 'sending' || verifyMailState() === 'sent'"
                    (click)="resendVerifyMail()">
              {{ 'verify_email_banner.resend' | translate }}
            </button>
            <button cButton color="secondary" variant="ghost" size="sm" type="button" (click)="dismissVerifyBanner()">
              {{ 'verify_email_banner.dismiss' | translate }}
            </button>
          </div>
        </div>
      </c-col>
    </c-row>
  }

  <!-- Newsletter opt-in: only rendered for users who unsubscribed, so this is
       their way back in without digging out an old mail. -->
  <c-row class="mb-4">
    <c-col xs="12">
      <app-newsletter-optin></app-newsletter-optin>
    </c-col>
  </c-row>

  <!-- User Level & eWallet -->
  @if (user(); as u) {
    <c-row class="mb-5">
      <c-col xs="12">
        <c-card class="level-card">
          <c-card-body>
            <!-- eWallet balance, per currency -->
            <div class="ewallet-balance d-flex align-items-center justify-content-between flex-wrap gap-2">
              <small class="text-muted mb-0">{{ 'dashboard_ewallet_balance' | translate }}</small>
              <div class="text-end">
                @if (nonZeroBalances(u); as bals) {
                  @for (b of bals; track b.currency) {
                    <h4 class="mb-0 text-success">{{ formatBalance(b.balance, b.currency) }}</h4>
                  } @empty {
                    <h4 class="mb-0 text-success">-</h4>
                  }
                }
              </div>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- Wolfpack migration notice: users with migrated products (LIC/NOTE/ADMIN) -->
    @if (hasWolfpackProducts()) {
      <c-row class="mb-4">
        <c-col xs="12">
          <c-alert color="warning" [dismissible]="false" class="wolfpack-migration-alert d-flex align-items-start">
            <svg cIcon name="cilWarning" size="xl" class="me-3 flex-shrink-0"></svg>
            <div class="flex-grow-1">
              <h4 class="alert-heading fw-bold mb-2">{{ 'wolfpack_migration_title' | translate }}</h4>
              <p class="fs-5 mb-3">{{ 'wolfpack_migration_notice' | translate }}</p>
              <button cButton color="primary" size="lg" class="fw-bold" (click)="goToWolfpack()">
                {{ 'wolfpack_migration_cta' | translate }}
              </button>
            </div>
          </c-alert>
        </c-col>
      </c-row>
    }

    <!-- KYC is no longer handled here — it is performed by the card issuer as part of the card creation process -->
    <c-row class="mb-4" *ngIf="u.kyc_status > 0">
      <c-col xs="12">
        <c-alert color="info" [dismissible]="false" class="d-flex align-items-center">
          <div class="flex-grow-1">
            <h5 class="alert-heading mb-2">{{ 'kyc_unavailable_title' | translate }}</h5>
            <p class="mb-0">{{ 'kyc_unavailable_text' | translate }}</p>
          </div>
        </c-alert>
      </c-col>
    </c-row>

  <c-row class="mb-4">
    <c-col xs="12">
      <c-card>
        <c-card-header class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <strong>{{ 'dashboard_my_cards_title' | translate }}</strong>
          <!-- Card partner programme: portal link, or how to buy a card. -->
          <a cButton color="primary" size="sm" [routerLink]="['/', currentLang(), 'mycards']">
            {{ 'mycards.widget_button' | translate }}
          </a>
        </c-card-header>
        <c-card-body>
          @if (cardsLoading()) {
            <div class="text-center py-4">
              <c-spinner color="primary"></c-spinner>
              <p class="mt-2">{{ 'dashboard_cards_loading' | translate }}</p>
            </div>
          }
          @if (!cardsLoading() && userCards().length === 0) {
            <div class="alert alert-info">
              {{ 'dashboard_no_cards' | translate }}
            </div>
          }
          @if (!cardsLoading() && userCards().length > 0) {
            <div class="table-responsive">
              <table cTable striped hover>
                <thead>
                  <tr>
                    <th>{{ 'dashboard_card_number' | translate }}</th>
                    <th>{{ 'dashboard_card_type' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_card_balance' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_card_turnover' | translate }}</th>
                    <th>{{ 'dashboard_card_created' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (card of userCards(); track card.id) {
                    <tr>
                      <td><strong>#{{ card.card_number }}</strong></td>
                      <td>
                        <span class="badge bg-{{ getCardTypeBadgeColor(card.card_type) }}">
                          {{ getCardTypeLabel(card.card_type) }}
                        </span>
                        @if (card.is_beta) {
                          <span class="badge bg-success ms-1">Beta</span>
                        }
                      </td>
                      <td class="text-end">{{ formatCurrency(card.balance) }}</td>
                      <td class="text-end">{{ formatCurrency(card.turnover) }}</td>
                      <td>{{ formatDate(card.created_at) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>
}
  <!-- Admin Fee Status -->
  @if (false && showAdminFeeStatus()) {
    <c-row class="mb-5">
      <c-col xs="12">
        @if (adminFeeExpired()) {
          <c-alert color="danger" [dismissible]="false" class="d-flex align-items-center">
            <svg cIcon name="cilBan" size="lg" class="me-3"></svg>
            <div class="flex-grow-1">
              <h5 class="alert-heading mb-2">{{ 'dashboard_admin_fee_expired_title' | translate }}</h5>
              <p class="mb-2">
                {{ 'dashboard_admin_fee_expired_message' | translate: { date: formatAdminFeeDate() } }}
              </p>
              <button
                cButton
                color="primary"
                size="sm"
                routerLink="/{{ currentLang() }}/order-wizard"
              >
                <svg cIcon name="cilCart" class="me-2"></svg>
                {{ 'dashboard_admin_fee_renew_button' | translate }}
              </button>
            </div>
          </c-alert>
        } @else if (adminFeeExpiringSoon()) {
          <c-alert color="warning" [dismissible]="false" class="d-flex align-items-center">
            <svg cIcon name="cilClock" size="lg" class="me-3"></svg>
            <div class="flex-grow-1">
              <h5 class="alert-heading mb-2">{{ 'dashboard_admin_fee_expiring_soon_title' | translate }}</h5>
              <p class="mb-2">
                {{ 'dashboard_admin_fee_expiring_soon_message' | translate: { date: formatAdminFeeDate() } }}
              </p>
              <button
                cButton
                color="dark"
                size="sm"
                routerLink="/{{ currentLang() }}/order-wizard"
              >
                <svg cIcon name="cilCart" class="me-2"></svg>
                {{ 'dashboard_admin_fee_renew_button' | translate }}
              </button>
            </div>
          </c-alert>
        } @else {
          <c-card class="admin-fee-card">
            <c-card-body>
              <div class="d-flex align-items-center">
                <svg cIcon name="cilCheck" size="lg" class="me-3 text-success"></svg>
                <div class="flex-grow-1">
                  <h5 class="mb-1">{{ 'dashboard_admin_fee_status' | translate }}</h5>
                  <p class="mb-0 text-muted">
                    {{ 'dashboard_admin_fee_paid_until' | translate }}:
                    <strong class="text-success">{{ formatAdminFeeDate() }}</strong>
                  </p>
                </div>
              </div>
            </c-card-body>
          </c-card>
        }
      </c-col>
    </c-row>
  }

  <!-- Referral Links (customer decision 2026-10-09 no. 4: shown again). The first
       link leads to the landing page, which carries ?ref into the direct checkout;
       the second (backend referral_url, /r/<id>) opens the checkout directly. -->
  @if (user(); as u) {
    <c-row class="mb-5">
      <c-col xs="12">
        <h4 class="referral-links-title mb-3">{{ 'dashboard_referral.title' | translate }}</h4>

        <h5 class="referral-link-name mb-1">{{ 'dashboard_referral.landing_label' | translate }}</h5>
        <p class="small text-body-secondary mb-2">{{ 'dashboard_referral.landing_hint' | translate }}</p>
        <app-referral-link class="d-block mb-4" [referralUrl]="landingReferralUrl()"></app-referral-link>

        @if (u.referral_url) {
          <h5 class="referral-link-name mb-1">{{ 'dashboard_referral.direct_label' | translate }}</h5>
          <p class="small text-body-secondary mb-2">{{ 'dashboard_referral.direct_hint' | translate }}</p>
          <app-referral-link class="d-block mb-4" [referralUrl]="u.referral_url"></app-referral-link>
        }
      </c-col>
    </c-row>
  }

  @if (false) { <!-- My Pools -->
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card>
          <c-card-header>
            <strong>{{ 'dashboard_my_pools_title' | translate }}</strong>
          </c-card-header>
          <c-card-body>
            @if (poolsLoading()) {
              <div class="text-center py-4">
                <c-spinner color="primary"></c-spinner>
                <p class="mt-2">{{ 'dashboard_pools_loading' | translate }}</p>
              </div>
            }
            @if (!poolsLoading() && userPools().length === 0) {
              <div class="alert alert-info">
                {{ 'dashboard_no_pools' | translate }}
              </div>
            }
            @if (!poolsLoading() && userPools().length > 0) {
              <div class="table-responsive">
                <table cTable striped hover>
                  <thead>
                    <tr>
                      <th>{{ 'dashboard_pool_id' | translate }}</th>
                      <th class="text-end">{{ 'dashboard_money_received' | translate }}</th>
                      <th class="text-end">{{ 'dashboard_max_payout' | translate }}</th>
                      <th class="text-end">{{ 'dashboard_percentage' | translate }}</th>
                      <th class="text-center">{{ 'user_pools.email_ok' | translate }}</th>
                      <th>{{ 'dashboard_started' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (pool of userPools(); track pool.id) {
                      <tr>
                        <td>
                          <span class="badge bg-info">
                            {{ 'dashboard_pool' | translate }} {{ pool.pool_id }}
                          </span>
                        </td>
                        <td class="text-end">{{ formatCurrency(pool.turnover) }}</td>
                        <td class="text-end">{{ formatCurrency(pool.max_balance) }}</td>
                        <td class="text-end">
                          <strong>{{ getPercentage(pool) }}%</strong>
                        </td>
                        <td class="text-center">
                          @if (!pool.needs_email) {
                            <span class="text-body-secondary">
                              {{ 'user_pools.email_not_required' | translate }}
                            </span>
                          } @else if (pool.has_emails) {
                            <span class="text-success fw-bold">OK</span>
                          } @else {
                            <span class="text-danger fw-bold">
                              {{ 'user_pools.email_not_ok' | translate }}
                            </span>
                          }
                        </td>
                        <td>{{ formatDate(pool.created_at) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
  
              @if (hasPoolsWithEmailNotOk()) {
                <c-alert color="warning" [dismissible]="false" class="d-flex align-items-center mt-3">
                  <svg cIcon name="cilExclamation" size="lg" class="me-3"></svg>
                  <div class="flex-grow-1">
                    <strong>{{ 'dashboard_pools_email_required_title' | translate }}</strong>
                    <p class="mb-0">
                      {{ 'dashboard_pools_email_required_message' | translate }}
                    </p>
                    <p class="mb-0 mt-2">
                      <strong>{{ 'dashboard_pools_email_status' | translate: { confirmed: confirmedEmailsCount(), required: requiredEmailsCount() } }}</strong>
                    </p>
                  </div>
                </c-alert>
              }
            }
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  }
  
    <!-- Community Widget -->
    <c-row class="mb-4">
      <c-col xs="12">
        <app-community-dashboard-widget></app-community-dashboard-widget>
      </c-col>
    </c-row>


  <!-- License Countdown -->
  @if (!presaleEnded() && domainId() === 0 && lic1Active()) {
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card class="license-countdown-card">
          <c-card-body class="text-center py-4">
            <h4 class="license-countdown-title mb-3">
              <svg cIcon name="cilClock" class="me-2"></svg>
              {{ 'license_countdown_title' | translate }}
            </h4>
            <p class="license-countdown-message mb-4">
              {{ 'license_countdown_message' | translate }}
            </p>
            <div class="countdown-container">
              <div class="countdown-display">
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownDays() }}</span>
                  <span class="countdown-label">{{ 'days' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownHours() }}</span>
                  <span class="countdown-label">{{ 'hours' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownMinutes() }}</span>
                  <span class="countdown-label">{{ 'minutes' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownSeconds() }}</span>
                  <span class="countdown-label">{{ 'seconds' | translate }}</span>
                </div>
              </div>
              <p class="deadline-date mt-3 mb-1">{{ 'license_countdown_deadline' | translate }}</p>
              <p class="timezone-info mb-0">{{ 'timezone_atlanta' | translate }}</p>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  }

  <!-- Latest Info Media -->
  @if (media(); as m) {
    <div class="info-media-widget">
      <c-row class="mb-4">
        <c-col>
          <media-card [media]="m" (click)="openMediaViewer(m)"></media-card>
        </c-col>
      </c-row>
      <c-row [routerLink]="['/', currentLang(), 'media', 'info']" class="mb-4">
        <c-col>
          <c-alert color="success" class="info">
            <svg cIcon name="info" class="me-2"></svg>
            <span [translate]="'menu.info'"></span>
          </c-alert>
        </c-col>
      </c-row>
    </div>
  }



  <!-- Quick Action Cards -->
  <c-row>
    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilCart" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_order_products_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_order_products_description' | translate }}
          </p>
          <button
            cButton
            color="primary"
            size="lg"
            class="w-100"
            routerLink="/{{ currentLang() }}/order-wizard"
          >
            <svg cIcon name="cilCart" class="me-2"></svg>
            {{ 'dashboard_start_order' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>

    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilUser" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_my_profile_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_my_profile_description' | translate }}
          </p>
          <button
            cButton
            color="success"
            variant="outline"
            size="lg"
            class="w-100"
            routerLink="/{{ currentLang() }}/profile"
          >
            <svg cIcon name="cilUser" class="me-2"></svg>
            {{ 'dashboard_view_profile' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>

    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilFile" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_my_orders_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_my_orders_description' | translate }}
          </p>
          <button
            cButton
            color="primary"
            variant="outline"
            size="lg"
            class="w-100"
            [routerLink]="['/', currentLang(), 'my-orders']"
          >
            <svg cIcon name="cilFile" class="me-2"></svg>
            {{ 'dashboard_view_orders' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Cross-site single sign-on: jump to our other platforms with one click 
  <c-row class="mb-4">
    <c-col xs="12">
      <app-cross-login></app-cross-login>
    </c-col>
  </c-row>
</c-container> -->